import React, { useState } from "react";
import { ResponsiveContainer, Sankey, Tooltip, Layer, Rectangle } from "recharts";
import { Workflow } from "lucide-react";
import { usePoll } from "@/hooks/usePoll";
import { endpoints } from "@/lib/api";
import { Panel, PageHeader, Loading } from "@/components/common";
import { num } from "@/lib/format";

const NODE_COLOR = (name) => {
  if (["Electricity", "Fuel"].includes(name)) return "#0f172a";
  if (["Refrigeration", "Boiler", "Process Equipment", "Packaging", "Compressed Air"].includes(name)) return "#2563eb";
  return "#10b981";
};

function SankeyNode({ x, y, width, height, index, payload }) {
  const color = NODE_COLOR(payload.name);
  const isRight = x > 400;
  return (
    <Layer key={`node-${index}`}>
      <Rectangle x={x} y={y} width={width} height={height} fill={color} fillOpacity={0.9} radius={2} />
      <text x={isRight ? x - 6 : x + width + 6} y={y + height / 2} textAnchor={isRight ? "end" : "start"}
        dominantBaseline="middle" fontSize="11" fill="#334155" fontWeight="500">
        {payload.name}
      </text>
    </Layer>
  );
}

export default function EnergyFlow() {
  const [metric, setMetric] = useState("energy");
  const { data } = usePoll(() => endpoints.sankey(metric), 6000, [metric]);
  if (!data) return <Loading />;

  return (
    <div data-testid="energy-flow-page">
      <PageHeader icon={Workflow} title="Energy & Resource Flow Center"
        subtitle="The Energy MRI — Source → Utility → Process. Watch energy & cost flow through the dairy."
        right={
          <div className="flex bg-slate-100 rounded-md p-0.5" data-testid="sankey-metric-toggle">
            {[["energy", "Energy (kW)"], ["cost", "Cost (₹/hr)"]].map(([m, label]) => (
              <button key={m} onClick={() => setMetric(m)} data-testid={`sankey-${m}`}
                className={`px-3 py-1.5 text-xs font-semibold rounded transition-colors ${metric === m ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}>
                {label}
              </button>
            ))}
          </div>
        } />

      <Panel testid="panel-sankey" title="Live Sankey" subtitle={`Flow weighted by ${metric === "energy" ? "energy (kW)" : "cost (₹/hr)"}`}>
        <ResponsiveContainer width="100%" height={460}>
          <Sankey
            data={data}
            node={<SankeyNode />}
            nodePadding={26}
            nodeWidth={12}
            link={{ stroke: metric === "energy" ? "#93c5fd" : "#6ee7b7", strokeOpacity: 0.35 }}
            margin={{ left: 10, right: 120, top: 10, bottom: 10 }}
          >
            <Tooltip formatter={(v) => num(v) + " " + data.unit} />
          </Sankey>
        </ResponsiveContainer>
      </Panel>

      <p className="mt-3 text-xs text-slate-500">
        Toggle between energy and cost to see how each utility and process contributes to the true cost of a litre of milk.
      </p>
    </div>
  );
}
